import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Popover } from '@/components/ui/popover';
import { classList } from '@/utils/cn';

import { useEditorFloatingContainer } from '../base-editor-context';
import { EditorBubbleItem, useEditorState } from '../components';

const TEXT_COLORS: BubbleColorMenuItem[] = [
  {
    name: 'Default',
    color: 'var(--foreground-default)',
  },
  {
    name: 'Purple',
    color: 'var(--purple-11)',
  },
  {
    name: 'Red',
    color: 'var(--red-11)',
  },
  {
    name: 'Yellow',
    color: 'var(--yellow-11)',
  },
  {
    name: 'Blue',
    color: 'var(--blue-11)',
  },
  {
    name: 'Green',
    color: 'var(--green-11)',
  },
  {
    name: 'Orange',
    color: 'var(--orange-11)',
  },
  {
    name: 'Pink',
    color: 'var(--pink-11)',
  },
  {
    name: 'Gray',
    color: 'var(--gray-11)',
  },
];

const HIGHLIGHT_COLORS: BubbleColorMenuItem[] = [
  {
    name: 'Default',
    color: 'transparent',
  },
  {
    name: 'Purple',
    color: 'var(--purple-4)',
  },
  {
    name: 'Red',
    color: 'var(--red-4)',
  },
  {
    name: 'Yellow',
    color: 'var(--yellow-4)',
  },
  {
    name: 'Blue',
    color: 'var(--blue-4)',
  },
  {
    name: 'Green',
    color: 'var(--green-4)',
  },
  {
    name: 'Orange',
    color: 'var(--orange-4)',
  },
  {
    name: 'Pink',
    color: 'var(--pink-4)',
  },
  {
    name: 'Gray',
    color: 'var(--gray-4)',
  },
];

export interface BubbleColorMenuItem {
  name: string;
  color: string;
}

interface ColorSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ColorSelector = ({
  open,
  onOpenChange: setOpen,
}: ColorSelectorProps) => {
  const editor = useEditorState();
  const container = useEditorFloatingContainer();

  if (!editor) return null;

  const activeColorItem = TEXT_COLORS.find(({ color }) =>
    editor.isActive('textStyle', { color })
  );

  const activeHighlightItem = HIGHLIGHT_COLORS.find(({ color }) =>
    editor.isActive('highlight', { color })
  );

  return (
    <Popover.Root modal={true} open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          size="xs"
          variant="ghost"
          className="gap-[3px] pl-0 pr-1 focus:ring-0 [&[data-state=open]>svg]:rotate-180"
          tooltip="Text color"
          tooltipOptions={{ sideOffset: 9 }}
          onPointerDown={(e) => {
            if (open) e.stopPropagation();
          }}
        >
          <span
            className={classList(
              'border border-border-strongest',
              'rounded-md smoothing-default',
              'flex items-center justify-center',
              'py-px',
              'font-medium size-6'
            )}
            style={{
              color: activeColorItem?.color,
              backgroundColor: activeHighlightItem?.color,
            }}
          >
            A
          </span>
          <Icons.chevronDown
            size="12px"
            className="text-foreground-muted transition-transform"
          />
        </Button>
      </Popover.Trigger>

      <Popover.Content
        container={container}
        portalled
        sideOffset={6}
        className={classList(
          'my-1 p-1',
          'max-h-80 w-48',
          'flex flex-col',
          'overflow-hidden overflow-y-auto',
          'rounded-lg smoothing-default',
          'border border-border-strong',
          'shadow-xl',
          'motion-scale-in-95 motion-opacity-in-0 motion-duration-200'
        )}
        align="start"
        style={{
          transformOrigin: 'var(--radix-popper-transform-origin)',
        }}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col">
          <div className="my-1 px-2 text-xs text-foreground-muted">Color</div>
          {TEXT_COLORS.map(({ name, color }, index) => (
            <EditorBubbleItem
              key={index}
              onSelect={() => {
                editor.commands.unsetColor();
                name !== 'Default' &&
                  editor
                    .chain()
                    .focus()
                    .setColor(color || '')
                    .run();
                setOpen(false);
              }}
              className={classList(
                'flex items-center justify-between',
                'px-2 py-1',
                'text-sm',
                'hover:bg-surface-active',
                'rounded-md smoothing-default'
              )}
            >
              <div className="flex items-center gap-2">
                <div
                  className={classList(
                    'border border-border-strongest',
                    'rounded-md smoothing-default',
                    'flex items-center justify-center',
                    'py-px',
                    'font-medium size-6'
                  )}
                  style={{ color }}
                >
                  A
                </div>
                <span>{name}</span>
              </div>
            </EditorBubbleItem>
          ))}
        </div>
        <div>
          <div className="my-1 px-2 text-xs text-foreground-muted">
            Background
          </div>
          {HIGHLIGHT_COLORS.map(({ name, color }, index) => (
            <EditorBubbleItem
              key={index}
              onSelect={() => {
                editor.commands.unsetHighlight();
                name !== 'Default' && editor.commands.setHighlight({ color });
                setOpen(false);
              }}
              className={classList(
                'flex items-center justify-between',
                'px-2 py-1',
                'text-sm',
                'hover:bg-surface-active',
                'rounded-md smoothing-default'
              )}
            >
              <div className="flex items-center gap-2">
                <div
                  className={classList(
                    'border border-border-strongest',
                    'rounded-md smoothing-default',
                    'flex items-center justify-center',
                    'py-px',
                    'font-medium size-6',
                    'text-transparent'
                  )}
                  style={{ backgroundColor: color }}
                >
                  A
                </div>
                <span>{name}</span>
              </div>
              {editor.isActive('highlight', { color }) && (
                <Icons.check className="h-4 w-4" />
              )}
            </EditorBubbleItem>
          ))}
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};
